import { useEffect, useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/shallow';
import { useSocketStore } from '../stores/socketStore';
import { useUserStore } from '../stores/userStore';
import { useProfileStore } from '../stores/profileStore';
import { getLogger } from '../utils/logger';
import { ConnectionStatus, EventHistoryItem, LabEventType, LpEventCountKey } from '../services/socket/types';

const logger = getLogger('socket');

interface UseSocketReturn {
  // Connection state
  connectionStatus: ConnectionStatus;
  isConnected: boolean;
  lastError: string | null;

  // Lab subscription state
  subscribedLabId: string | null;

  // Event tracking
  eventHistory: EventHistoryItem[];
  eventCounts: Record<LabEventType, number>;

  // LP event tracking
  lpEventCounts: Record<LpEventCountKey, number>;
  setLpTracking: (enabled: boolean) => void;
  clearLpEventCounts: () => void;

  // Ping/pong health
  pingHealth: {
    lastPongReceived: number;
    missedPongs: number;
    isHealthy: boolean;
  };

  // Actions
  reconnect: () => Promise<void>;
  subscribeLab: (labId: string) => Promise<void>;
  unsubscribeLab: () => Promise<void>;

  // Debug controls
  clearEventHistory: () => void;
}

export const useSocket = (disabled = false): UseSocketReturn => {
  const user = useUserStore(state => state.user);
  const selectedLab = useProfileStore(state => state.getSelectedLab());

  // Individual selectors — each only re-renders when its specific value changes
  const connectionStatus = useSocketStore(s => s.connectionStatus);
  const isInitialized = useSocketStore(s => s.isInitialized);
  const lastError = useSocketStore(s => s.lastError);
  const subscribedLabId = useSocketStore(s => s.subscribedLabId);
  const eventHistory = useSocketStore(s => s.eventHistory);
  const eventCounts = useSocketStore(s => s.eventCounts);
  const lpEventCounts = useSocketStore(s => s.lpEventCounts);
  const pingHealth = useSocketStore(s => s.pingHealth);

  // Actions — stable references, useShallow prevents the wrapper object from triggering re-renders
  const {
    initialize,
    cleanup,
    reconnect,
    subscribeLab,
    unsubscribeLab,
    clearEventHistory,
    clearLpEventCounts,
    setLpTracking,
    isConnected,
  } = useSocketStore(useShallow(s => ({
    initialize: s.initialize,
    cleanup: s.cleanup,
    reconnect: s.reconnect,
    subscribeLab: s.subscribeLab,
    unsubscribeLab: s.unsubscribeLab,
    clearEventHistory: s.clearEventHistory,
    clearLpEventCounts: s.clearLpEventCounts,
    setLpTracking: s.setLpTracking,
    isConnected: s.isConnected,
  })));

  // Initialize socket connection when user is authenticated
  useEffect(() => {
    if (disabled) {
      if (isInitialized) {
        logger.debug(' Socket disabled (maintenance mode), cleaning up');
        cleanup();
      }
      return;
    }
    if (user && !isInitialized) {
      logger.debug(' User authenticated, initializing socket connection', {
        timestamp: Date.now(),
        userId: user.id,
      });
      initialize();
    } else if (!user && isInitialized) {
      logger.debug(' User logged out, cleaning up socket connection');
      cleanup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isInitialized, disabled]); // Functions from Zustand are stable, don't include them

  // Auto-subscribe to selected lab
  useEffect(() => {
    // Early return if not connected or no change needed
    if (!isConnected()) return;

    if (selectedLab && selectedLab.id !== subscribedLabId) {
      logger.debug(' Lab changed, subscribing to:', {
        newLab: selectedLab.id,
        previousLab: subscribedLabId,
        timestamp: Date.now(),
      });
      subscribeLab(selectedLab.id);
    } else if (!selectedLab && subscribedLabId) {
      logger.debug(' No lab selected, unsubscribing');
      unsubscribeLab();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLab?.id, subscribedLabId, connectionStatus]); // Functions from Zustand are stable, don't include them

  // Connection status notifications are handled by ConnectionStatusBanner component

  // Memoized connection status
  const connected = useMemo(() => connectionStatus === 'connected', [connectionStatus]);

  return {
    // Connection state
    connectionStatus,
    isConnected: connected,
    lastError,

    // Lab subscription state
    subscribedLabId,

    // Event tracking
    eventHistory,
    eventCounts,

    // LP event tracking
    lpEventCounts,
    setLpTracking,
    clearLpEventCounts,

    // Ping/pong health
    pingHealth,

    // Actions
    reconnect: useCallback(async () => {
      logger.debug(' Manual reconnect requested');
      await reconnect();
    }, [reconnect]),

    subscribeLab: useCallback(async (labId: string) => {
      logger.debug(' Manual lab subscription:', labId);
      await subscribeLab(labId);
    }, [subscribeLab]),

    unsubscribeLab: useCallback(async () => {
      logger.debug(' Manual lab unsubscription');
      await unsubscribeLab();
    }, [unsubscribeLab]),

    // Debug controls
    clearEventHistory: useCallback(() => {
      logger.debug(' Clearing event history');
      clearEventHistory();
    }, [clearEventHistory]),
  };
};